import dcmjs from "dcmjs";
import { useParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { useSnapshot } from "valtio";

import useViewerParams from "shared/hooks/useViewerParams";
import { ENTRYPOINT } from "config/api";
import { transformPath } from "lib/cornerstone/helpers/niftiDiscovery";

import viewerState, {
  isNiftiMetadata,
  Segment,
  updateSegmentationVisibility,
  clearSeriesColors,
} from "stores/viewer";

import {
  calculatePercentiles,
  calculateZscore,
  fetchRegressionModels,
  getAgeFromMetadata,
  getPercentileRange,
  RegressionModel,
} from "lib/client/normative";

import { Marker } from "features/patients/quantification/components/Marker";
import {
  getAllDescendant,
  getSegmentRID,
  markers,
} from "shared/utils/recursive";
import useStudyMetadata from "./useStudyMetadata";
import { dicomOptions } from "lib/client/dicomweb";
import { switchNiftiBase } from "lib/cornerstone/setupCornerstoneViewer";

function useMarkers(studyUID?: string) {
  const { segmentsPerSeries } = useSnapshot(viewerState);

  const { study_id } = useParams();
  const studyData = useStudyMetadata(studyUID || study_id);
  const { patient_id, acquisition } = useViewerParams();

  const {
    sex,
    naturalizedDatasets,
    isLoading: isStudyLoading,
    version,
  } = studyData;

  const isNiftiMode = isNiftiMetadata(viewerState.instanceMetadata);

  // =======================================================================
  // 1. get Clinical Info JSON
  // =======================================================================
  const { data: clinicalData } = useQuery(
    ["clinical_json", patient_id, acquisition],
    async () => {
      const diskResp = await fetch(
        `${ENTRYPOINT}/patients/${patient_id}/disk_items/${acquisition}`,
        { credentials: "include" },
      );
      if (!diskResp.ok) return null;
      const diskJ = await diskResp.json();
      const paths: string[] = Array.isArray(diskJ)
        ? diskJ
        : diskJ.items ?? diskJ.disk_items ?? diskJ.files ?? [];

      const clinicalPath = paths.find((p) => /clinical_info.*\.json$/.test(p));
      if (!clinicalPath) return null;

      const fileResp = await fetch(
        `${ENTRYPOINT}${transformPath(clinicalPath)}`,
        { credentials: "include" },
      );
      if (!fileResp.ok) return null;
      return await fileResp.json();
    },
    {
      enabled: !!patient_id && !!acquisition && isNiftiMode,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  // =======================================================================
  // 2. get Observation JSON
  // =======================================================================
  const { data: observationData } = useQuery(
    ["observation_json", patient_id, acquisition],
    async () => {
      const diskResp = await fetch(
        `${ENTRYPOINT}/patients/${patient_id}/disk_items/${acquisition}`,
        { credentials: "include" },
      );
      if (!diskResp.ok) return null;
      const diskJ = await diskResp.json();
      const paths: string[] = Array.isArray(diskJ)
        ? diskJ
        : diskJ.items ?? diskJ.disk_items ?? diskJ.files ?? [];

      const obsPaths = paths.filter((p) => /_observation\.json$/.test(p));
      if (obsPaths.length === 0) return null;

      const fetchPromises = obsPaths.map(async (obsPath) => {
        const url = `${ENTRYPOINT}${transformPath(obsPath)}`;
        const fileResp = await fetch(url, { credentials: "include" });
        if (!fileResp.ok) return null;
        return await fileResp.json();
      });

      const results = await Promise.all(fetchPromises);

      const mergedComponent: any[] = [];
      let device: any = undefined;

      results.forEach((res) => {
        if (res && res.resourceType === "Observation") {
          if (Array.isArray(res.component))
            mergedComponent.push(...res.component);
          if (!device && res.device) device = res.device;
        }
      });

      return { component: mergedComponent, device };
    },
    {
      enabled: !!patient_id && !!acquisition && isNiftiMode,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  const mockMeta = useMemo(() => {
    if (naturalizedDatasets?.[0]) return naturalizedDatasets[0];
    const birthDate = clinicalData?.scan?.birthDate?.replace(/-/g, "");
    const studyDate = clinicalData?.scan?.acquisition?.replace("ses-", "");
    const patAge = clinicalData?.subject?.subject_age
      ? `${clinicalData.subject.subject_age}Y`
      : undefined;

    return {
      PatientBirthDate: birthDate,
      StudyDate: studyDate,
      PatientAge: patAge,
      PatientSex: clinicalData?.subject?.subject_sex,
    } as any;
  }, [naturalizedDatasets, clinicalData]);

  const exactAge = useMemo(
    () => getAgeFromMetadata(mockMeta, true),
    [mockMeta],
  );

  const safeSex = sex || mockMeta?.PatientSex || "M";
  const obsVersion = observationData?.device?.modelNumber?.split(" ")?.[0];
  const safeVersion = version || obsVersion;

  const { data: regressionModels, isLoading: isRegressionModelLoading } =
    useQuery(
      ["regression", "all", safeSex, safeVersion] as const,
      ({ queryKey }) =>
        fetchRegressionModels(queryKey[2] as any, queryKey[3] as any),
      { enabled: !!safeSex, ...dicomOptions },
    );

  const populatedMarkers = useMemo(
    () =>
      populate(
        naturalizedDatasets,
        segmentsPerSeries as Segment[],
        regressionModels,
        observationData,
        exactAge,
      ),
    [
      naturalizedDatasets,
      segmentsPerSeries,
      regressionModels,
      observationData,
      exactAge,
    ],
  );

  const displaySegment = useCallback(
    (
      { seriesUID, segments }: Pick<Marker, "seriesUID" | "segments">,
      onlySelect: boolean = false,
    ) => {
      const isNifti = isNiftiMetadata(viewerState.instanceMetadata);

      function applySegmentSelection() {
        const targetRIDs = segments
          .map((s) => getSegmentRID(s as Segment))
          .filter(Boolean);

        const isSelected = viewerState.segmentsPerSeries
          .filter((seg) => targetRIDs.includes(getSegmentRID(seg as Segment)))
          .some((segment) => segment.selected);

        const parentMarkers = markers.filter(
          (m) =>
            m.substructures &&
            m.substructures.some((sub) => targetRIDs.includes(sub)),
        );
        const parentRIDs = parentMarkers.map((m) => m.code);

        viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
          (segment) => {
            const segRID = getSegmentRID(segment as Segment);

            if (targetRIDs.includes(segRID)) {
              segment.selected = onlySelect ? true : !isSelected;
            } else if (parentRIDs.includes(segRID)) {
              segment.selected = false;
            }
            return segment;
          },
        );
      }

      if (isNifti && seriesUID) {
        const segModality = seriesUID.replace("nifti:seg:", "");
        const currentModality =
          (viewerState.niftiActiveModality as string) ??
          (viewerState.instanceMetadata as any)?._activeModality;

        if (
          segModality !== currentModality &&
          (viewerState.niftiBases as any)?.[segModality]
        ) {
          const newSeriesUID = `nifti:seg:${segModality}`;
          const prevSeriesUID = `nifti:seg:${currentModality}`;

          viewerState.pendingSeriesUID = newSeriesUID;

          viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
            (seg) =>
              seg.seriesUID === prevSeriesUID
                ? { ...seg, selected: false }
                : seg,
          );
          viewerState.niftiActiveModality = segModality;
          applySegmentSelection();

          clearSeriesColors(newSeriesUID);

          const newBaseUrl = (viewerState.niftiBases as any)[segModality];
          switchNiftiBase(newBaseUrl)
            .then(() => {
              viewerState.pendingSeriesUID = null;
              return updateSegmentationVisibility();
            })
            .catch((err) => {
              viewerState.pendingSeriesUID = null;
              console.error(err);
            });

          return;
        }
      }

      if (
        !isNifti &&
        viewerState.instanceMetadata?.SeriesInstanceUID !== seriesUID
      ) {
        viewerState.instanceMetadata = naturalizedDatasets.find(
          (instance) => instance.SeriesInstanceUID === seriesUID,
        );
      }

      applySegmentSelection();
      updateSegmentationVisibility().catch(console.error);
    },
    [naturalizedDatasets],
  );

  return {
    ...studyData,
    isStudyLoading,
    isRegressionModelLoading,
    displaySegment,
    naturalizedDatasets,
    populatedMarkers,
    regressionModels,
  };
}

// ============ populate + helpers ============

function parseDataFromObservationJson(
  observationJson: any,
): [string, number, string][] {
  const data: [string, number, string][] = [];
  if (!observationJson || !Array.isArray(observationJson.component))
    return data;

  observationJson.component.forEach((comp: any) => {
    const code = comp.coding?.[0]?.code;
    const value = comp.valueQuantity?.value;
    const unit = comp.valueQuantity?.code;

    if (code && value !== undefined && unit) {
      data.push([code, value, unit]);
    }
  });

  if (observationJson.bodySite?.code && observationJson.valueQuantity) {
    const code = observationJson.bodySite.code;
    const value = observationJson.valueQuantity.value;
    const unit = observationJson.valueQuantity.code;
    if (code && value !== undefined && unit) {
      data.push([code, value, unit]);
    }
  }

  return data;
}

function populate(
  naturalizedDatasets: dcmjs.data.NaturalizedDataset[],
  segmentsPerSeries: Segment[],
  regressionModels: Record<string, RegressionModel> | any,
  observationData?: any,
  exactAge?: number | null,
): Marker[] {
  let quantitativeData: [string, number, string][] = [];

  if (
    observationData &&
    observationData.component &&
    observationData.component.length > 0
  ) {
    quantitativeData = parseDataFromObservationJson(observationData);
  } else {
    quantitativeData = parseQuantitativeDataFromSR(naturalizedDatasets);
  }

  const modelsDict = regressionModels?.models || regressionModels;

  function getQuantitativeAndNormativeData(code: string) {
    const quant = quantitativeData?.filter((m) => m[0] === code);
    const icv = quant?.find((m) => m[2] === "%")?.[1];
    const mL = quant?.find((m) => m[2] === "cm3" || m[2] === "mL")?.[1];
    const regressionModel = modelsDict?.[code];

    const isValidModel = Boolean(
      regressionModel &&
        Array.isArray(regressionModel.extent) &&
        regressionModel.extent.length > 0,
    );
    const hasNorm = isValidModel;
    const hasQuantif = Boolean(quant?.length);

    let zscore, percentileRange;
    if (exactAge && isValidModel && icv !== undefined) {
      try {
        zscore = calculateZscore(regressionModel, exactAge, icv);
        const percentiles = calculatePercentiles(regressionModel, exactAge);
        percentileRange = getPercentileRange(percentiles, icv);
      } catch (e) {
        console.warn(`[Normative] calculate failed for ${code}:`, e);
      }
    }

    return { icv, mL, zscore, percentileRange, hasNorm, hasQuantif };
  }

  const seriesUIDs = Array.from(
    new Set(segmentsPerSeries.map((segment) => segment.seriesUID)),
  );
  const populatedMarkers = seriesUIDs.flatMap((seriesUID) => {
    const segments = segmentsPerSeries.filter(
      (seg) => seg.seriesUID === seriesUID,
    );
    return getHierarchyMarkers(
      getQuantitativeAndNormativeData,
      segments,
      exactAge,
      modelsDict,
    );
  });

  const unknownSegMarkers: Marker[] = segmentsPerSeries
    .filter(
      (seg) =>
        !markers.some(
          (marker) => getSegmentRID(seg as Segment) === marker.code,
        ),
    )
    .map((seg) => {
      const code = getSegmentRID(seg);
      const q = getQuantitativeAndNormativeData(code);
      const marker: Marker = {
        code,
        ...q,
        segments: [seg],
        seriesUID: seg?.seriesUID,
        isUnknown: true,
      };
      return marker;
    });
  populatedMarkers.push(...unknownSegMarkers);

  const coordinates = parseCoordsFromSR(naturalizedDatasets);
  if (coordinates?.length) {
    const coordMarkers: Marker[] = coordinates.flatMap(
      ({ related, uid, coord, rids }) => {
        return populatedMarkers
          .filter((m) => m.code === related)
          ?.map((relatedMarker) => {
            const code = `${related}:${uid}:${rids.join(":")}`;
            const coordMarker: Marker = { code, coord, hasNorm: false };
            if (relatedMarker) {
              relatedMarker["substructures"] = [
                ...(relatedMarker.substructures || []),
                code,
              ];
              relatedMarker["hasCoords"] = true;
              coordMarker["segments"] = relatedMarker["segments"];
              coordMarker["seriesUID"] = relatedMarker["seriesUID"];
            }
            return coordMarker;
          });
      },
    );
    populatedMarkers.push(...coordMarkers);
  }

  return populatedMarkers;
}

function getHierarchyMarkers(
  getQuantitativeAndNormativeData: Function,
  segmentsPerSeries: Segment[],
  age?: number | null,
  modelsDict?: Record<string, RegressionModel> | any,
) {
  const initialMarkers = markers.map((marker) => {
    const q = getQuantitativeAndNormativeData(marker.code);
    const segment = segmentsPerSeries.find(
      (seg) => getSegmentRID(seg) === marker.code,
    );
    const hasSegments = Boolean(segment);
    return {
      ...marker,
      ...q,
      seriesUID: segment?.seriesUID,
      segments: [segment],
      hasSegments,
    };
  });

  const getSum = (marker: any, key: "mL" | "icv"): number | undefined => {
    if (marker[key] !== undefined && marker[key] !== null) return marker[key];
    if (!marker.substructures?.length) return undefined;

    let sum = 0;
    let hasValue = false;
    for (const rid of marker.substructures) {
      const sub = initialMarkers.find((m) => m.code === rid);
      if (sub) {
        const subVal = getSum(sub, key);
        if (subVal !== undefined) {
          sum += subVal;
          hasValue = true;
        }
      }
    }
    return hasValue ? Number(sum.toFixed(4)) : undefined;
  };

  return initialMarkers
    .map((marker: Marker) => {
      if (!marker.substructures) return marker;

      const subs: Marker[] = getAllDescendant(marker.code)
        .map((rid) => initialMarkers.find((m) => m.code === rid))
        .filter(Boolean) as Marker[];

      const descSegments = subs.flatMap((m) => m.segments).filter((s) => s);
      const ownSegments = (marker.segments ?? []).filter((s) => s);
      const subSegments = [...ownSegments, ...descSegments];

      const hasSegments = !!subSegments.length;
      const hasQuantif = subs.some((m) => m.hasQuantif) || marker.hasQuantif;
      const hasNorm = subs.some((m) => m.hasNorm) || marker.hasNorm;

      let finalML = getSum(marker, "mL");
      let finalICV = getSum(marker, "icv");

      let zscore = marker.zscore;
      let percentileRange = marker.percentileRange;

      const regressionModel = modelsDict?.[marker.code];
      const isValidModel = Boolean(
        regressionModel &&
          Array.isArray(regressionModel.extent) &&
          regressionModel.extent.length > 0,
      );

      if (
        age &&
        isValidModel &&
        finalICV !== undefined &&
        marker.icv === undefined
      ) {
        try {
          zscore = calculateZscore(regressionModel, age, finalICV);
          const percentiles = calculatePercentiles(regressionModel, age);
          percentileRange = getPercentileRange(percentiles, finalICV);
        } catch (e) {
          console.warn(
            `[Normative] getHierarchyMarkers calculate failed for ${marker.code}:`,
            e,
          );
        }
      }

      return {
        ...marker,
        seriesUID: subSegments[0]?.seriesUID ?? marker.seriesUID,
        segments: subSegments,
        hasQuantif,
        hasSegments,
        hasNorm: isValidModel || hasNorm,
        mL: finalML,
        icv: finalICV,
        zscore,
        percentileRange,
      };
    })
    .filter((marker) => marker.seriesUID);
}

function getImageMeasurements(
  naturalizedDatasets: dcmjs.data.NaturalizedDataset[],
) {
  return naturalizedDatasets
    ?.find((v) => v.Modality === "SR" && v.Manufacturer === "Qynapse")
    ?.ContentSequence?.find(
      (content) =>
        content.ConceptNameCodeSequence?.[0]?.CodeMeaning ===
        "Imaging Measurements",
    );
}

export function parseQuantitativeDataFromSR(
  naturalizedDatasets: dcmjs.data.NaturalizedDataset[],
): [string, number, string][] {
  const imagingMeasurements = getImageMeasurements(naturalizedDatasets);
  return imagingMeasurements?.ContentSequence?.flatMap((c) => {
    const codes = c.ContentSequence?.map((item) =>
      item.ValueType === "CODE"
        ? item.ConceptCodeSequence?.[0].CodeValue
        : null,
    ).filter(Boolean);
    const measures = c.ContentSequence?.filter(
      (item) => item.ValueType === "NUM",
    );
    return measures.map((measure) => {
      const value = measure?.MeasuredValueSequence?.[0]?.NumericValue;
      const unit =
        measure?.MeasuredValueSequence?.[0]?.MeasurementUnitsCodeSequence?.[0]
          .CodeValue;
      return [codes.join(":"), value, unit] as [string, number, string];
    });
  });
}

export function parseCoordsFromSR(
  naturalizedDatasets: dcmjs.data.NaturalizedDataset[],
) {
  const imagingMeasurements = getImageMeasurements(naturalizedDatasets);
  const info = imagingMeasurements?.ContentSequence.filter((c) =>
    c.ContentSequence.some((c1) => c1.ValueType === "SCOORD3D"),
  );
  const coordsInfo = info?.map((i) => {
    const coord = i.ContentSequence.find(
      (c) => c.ValueType === "SCOORD3D",
    ).GraphicData;
    const label = i.ContentSequence.find(
      (c) => c.ValueType === "TEXT",
    ).TextValue;
    const uid = i.ContentSequence.find((c) => c.ValueType === "UIDREF").UID;
    const rids = i.ContentSequence.filter((c) => c.ValueType === "CODE").map(
      (c) => c.ConceptCodeSequence?.[0].CodeValue,
    );
    return { coord, label, uid, related: "RID4700", rids };
  });
  return coordsInfo;
}

export default useMarkers;
